import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { DocumentNode } from "@apollo/client";

import FormFieldClaimMapping, { EMPTY_CLAIM_MAPPING_VALUE } from "components/FormFields/ClaimMapping";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import { ApiKey, ApiKeyType } from "types/generated";
import { stringIsRequired } from "utils/formValidators";
import { withTestId } from "utils/withTestId";

import { EditApiKeyFields } from "./types";
import { useUpdateApiKey } from "../hooks/useUpdateApiKey";

type ApiKeysEditDrawerProps = {
  apiKey: Pick<ApiKey, "id" | "description" | "name" | "teams" | "type" | "oidcSettings">;
  refetchQueriesOnUpdate?: DocumentNode[];
};

const ApiKeysEditDrawer = createDrawer(
  ({ apiKey, refetchQueriesOnUpdate }: ApiKeysEditDrawerProps) => {
    const drawer = useModal();

    const { onUpdate, loading: isSubmitting } = useUpdateApiKey({
      apiKeyId: apiKey.id,
      callback: () => drawer.hide(),
      refetchQueries: refetchQueriesOnUpdate,
    });

    const isOidc = apiKey.type === ApiKeyType.Oidc;

    const editForm = useForm<EditApiKeyFields>({
      defaultValues: {
        description: apiKey.description || "",
        name: apiKey.name || "",
        claimMapping:
          isOidc && apiKey.oidcSettings?.claimMapping?.entries?.length
            ? apiKey.oidcSettings.claimMapping.entries
            : [EMPTY_CLAIM_MAPPING_VALUE],
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
    } = editForm;

    const handleCloseDrawer = () => {
      drawer.hide();
    };

    const handleSubmitClick = () => {
      handleSubmit(onSubmit)();
    };

    const onSubmit: SubmitHandler<EditApiKeyFields> = (formFields) => {
      const filteredClaimMapping = formFields.claimMapping.filter(
        (claim) => claim.name.trim() && claim.value.trim()
      );

      const input = {
        name: formFields.name.trim(),
        description: formFields.description.trim(),
        teams: apiKey.teams,
        oidcClaimMappings: isOidc
          ? { entries: filteredClaimMapping }
          : null,
      };
      onUpdate(input);
    };

    return (
      <DrawerForm isDirty={isDirty} {...withTestId("api-key-edit-drawer")}>
        <FormProvider {...editForm}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Edit details" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            <FormField label="Name" error={errors?.name?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter API key name"
                  error={!!errors?.name}
                  {...register("name", {
                    validate: stringIsRequired("Name field is required."),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter API key description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            {isOidc && (
              <Box margin="large 0 0 0" direction="column" gap="medium">
                <FormFieldClaimMapping />
                <Banner variant="info">
                  Currently, only the <strong>groups</strong> Spacelift claim name is supported. Any
                  other claim names will be ignored.
                </Banner>
              </Box>
            )}

            <DrawerFooter>
              <DrawerFooterActions>
                <Button variant="secondary" onPress={handleCloseDrawer}>
                  Cancel
                </Button>
                <Button variant="primary" onPress={handleSubmitClick} loading={isSubmitting}>
                  Save
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showApiKeysEditDrawer = createDrawerTrigger(ApiKeysEditDrawer);
